import {
    AuthState,
    ConfirmLoginRequest,
    ConfirmLoginResponse,
    ForgotPassword,
    ForgotPasswordResponse,
    LoginRequest,
    RefreshResponse,
    RegisterDirectClientRequest,
    RegisterDirectClientResponse,
    ResendOtpRequest,
    ResetPasswordRequest,
    ResetPasswordResponse,
    SetPasswordRequest,
    SetPasswordResponse,
    UserDataResponse
} from 'Application/models';
import {
    authForgotPass,
    authResetPassword,
    authSetPassword,
    confirmLogin,
    getUserData,
    loginUser,
    logout,
    refreshToken,
    registerDirectClient,
    resendOtp
} from 'Application/service';
import { deleteUserDataFromStorage, saveUserDataToStorage } from 'Application/utils/AppSessionStorage';
import { create } from 'zustand';

export const useAuthStore = create<AuthState>((set) => ({
    isLoading: false,
    isError: false,
    isDownloadFileLoading: false,
    error: null,
    userData: null,
    authData: null,
    forgotPasswordData: null,
    resetPasswordData: null,
    setPasswordData: null,

    login: async (loginData: LoginRequest) => {
        set({ isLoading: true });

        try {
            await loginUser(loginData);

            set({ isError: false, error: null });
        } catch (error) {
            set({ error: 'Failed to log user in', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    confirmLogin: async (confirmLoginData: ConfirmLoginRequest) => {
        set({ isLoading: true });

        try {
            const confirmLoginResponse: ConfirmLoginResponse = await confirmLogin(confirmLoginData);
            set({ authData: confirmLoginResponse, isError: false, error: null });

            await useAuthStore.getState().getUserData();
        } catch (error) {
            set({ error: 'Failed to verify otp', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getUserData: async () => {
        set({ isLoading: true });

        try {
            const userDataResponse: UserDataResponse = await getUserData();
            set({ userData: userDataResponse, isError: false, error: null });
        } catch (error) {
            set({ error: 'Failed to get user info', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    refreshToken: async () => {
        set({ isLoading: true });

        try {
            const response: RefreshResponse = await refreshToken();
            set({ authData: response, isError: false, error: null });
        } catch (error) {
            set({ error: 'Failed to refresh tokens', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    resendOtp: async (resendOtpData: ResendOtpRequest) => {
        set({ isLoading: true });

        try {
            await resendOtp(resendOtpData);
            set({ isError: false, error: null });
        } catch (error) {
            set({ error: 'Failed to resend OTP', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    logout: async () => {
        try {
            logout();
            set({ userData: null });
            deleteUserDataFromStorage();
        } catch (error) {
            set({ error: 'Failed to log user out', isError: true });
            throw error;
        }
    },

    forgotPassword: async (forgotPasswordData: ForgotPassword) => {
        set({ isLoading: true });

        try {
            const response: ForgotPasswordResponse = await authForgotPass(forgotPasswordData.email);
            set({ forgotPasswordData: response, isError: false, error: null });
        } catch (error) {
            // Set error message from the response
            const errorMessage = error?.response?.data?.message || 'Failed to send forgot password request';
            set({ error: errorMessage, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    resetPassword: async (resetPasswordData: ResetPasswordRequest) => {
        set({ isLoading: true });

        try {
            const response: ResetPasswordResponse = await authResetPassword(resetPasswordData);
            set({ resetPasswordData: response, isError: false, error: null });
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Failed to reset password';
            set({ error: errorMessage, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    setPassword: async (setPasswordData: SetPasswordRequest) => {
        set({ isLoading: true });

        try {
            const response: SetPasswordResponse = await authSetPassword(setPasswordData);
            set({ resetPasswordData: response, isError: false, error: null });
        } catch (error) {
            const errorMessage = error?.response?.data?.message || 'Failed to reset password';
            set({ error: errorMessage, isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    registerDirectClient: async (directClientRegistrationData: RegisterDirectClientRequest) => {
        set({ isLoading: true });

        try {
            const response: RegisterDirectClientResponse = await registerDirectClient(directClientRegistrationData);
            set({ isError: false, error: null });
            return response;
        } catch (error) {
            throw error;
        } finally {
            set({ isLoading: false });
        }
    }
}));
